// src/hooks/outputEntitlementsStore.js
//
// The module-level half of useOutputEntitlements: a ONE-ENTRY cache keyed by
// tenant, plus the epoch/listener pair that lets a run refusal caught in the
// orchestrator refresh the wizard's locks without threading a setter through
// six call sites. Same reasoning — and same shape — as the registry in
// utils/toneMigration.js and the epoch block in hooks/useEntitlements.js.
//
// ⛔⛔ THE CACHE EXISTS FOR ONE RULE: a 503 must never render as "you have
// none". It means "we could not read your entitlements", not "you are entitled
// to nothing", and greying every output type for a customer whose plan is fine
// is the worst outcome this feature can produce. So `markStale` KEEPS the
// previous answer and nothing ever writes an empty set on a failure.
//
// Keyed by tenant so a late write can never leak across a workspace switch:
// readCache answers null for any other tenant, which is "not known" (fail
// open), never "nothing granted".

/** @type {{tenantId: string, variants: string[]|null, reason: string|null,
 *          planId: string|null, grantKey: string|null, stale: boolean}|null} */
let cache = null;

let epoch = 0;
const listeners = new Set();

function publish() {
  epoch += 1;
  listeners.forEach(fn => fn(epoch));
}

/** Current epoch — the initial value for a hook's subscription state. */
export function getEpoch() {
  return epoch;
}

/** Subscribe to refresh nudges. Returns the unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The cached answer for THIS tenant, or null when there is none. */
export function readCache(tenantId) {
  if (!tenantId || !cache || cache.tenantId !== tenantId) return null;
  return cache;
}

/** ⭐ Called ONLY on a 200. Nothing else may write the cache. */
export function writeCache(tenantId, entry) {
  if (!tenantId) return null;
  cache = { tenantId, stale: false, ...entry };
  return cache;
}

/**
 * A 503 landed: KEEP the previous answer and flag it stale.
 * @returns the kept entry, or null when there was nothing to keep.
 */
export function markStale(tenantId) {
  const current = readCache(tenantId);
  if (!current) return null;
  cache = { ...current, stale: true };
  return cache;
}

/** Drop everything (teardown, tests). */
export function clearCache() {
  cache = null;
}

/**
 * Re-read /entitlements on every mounted hook instance.
 *
 * Called after a run refusal that is a verdict about output types — 402
 * FEATURE_NOT_IN_PLAN or 403 APP_NOT_ASSIGNED — so the wizard's locks catch
 * up. ⛔ The refusal's own `details.granted` is NOT used for this: it carries
 * prefixed ids with the wildcard unexpanded.
 */
export function refreshOutputEntitlements() {
  publish();
}
